import { useEffect, useRef, useState } from "react";
import { Globe, GitBranch, Plus, Trash2 } from "lucide-react";

import { Picker } from "./Picker.jsx";
import { api } from "../lib/api.js";

/**
 * The deploy manifest, as a form.
 *
 * It used to be a textarea holding the raw JSON, on the reasoning that the
 * document is the honest representation and a generated form would hide more than
 * it helped. That was wrong in practice: configuring a project meant knowing the
 * schema by heart, a misplaced comma failed the whole save, and nothing told you
 * what a field was for until the server rejected it.
 *
 * So: real controls, laid out in the order a deploy actually happens — where the
 * code comes from, how it is built, what is checked, what is run. The help text
 * comes from `GET /projects/schema`, which is the same schema the validator and
 * the runner use, so this cannot drift from what the server will accept.
 *
 * The JSON is still there, under Advanced, because a field this form does not
 * know about must remain editable rather than becoming unreachable.
 */

/**
 * An input with suggestions behind it.
 *
 * A native `<datalist>` rather than a hand-built combobox: it is typed *and*
 * chosen, which is what these fields need — a domain that is not in Cloudflare
 * yet is still a legitimate answer — and it is accessible and works on a phone
 * without any of it being my code.
 */
function Suggest({ value, onChange, options = [], id, placeholder, className = "input-mono" }) {
    return (
        <>
            <input className={className} list={id} value={value ?? ""} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
            <datalist id={id}>
                {options.map((option) => (
                    <option key={option.value ?? option} value={option.value ?? option}>{option.label ?? ""}</option>
                ))}
            </datalist>
        </>
    );
}

/**
 * A path, completed against the filesystem as you type.
 *
 * Confined server-side to the roots a project can live in. Typing stays possible:
 * a directory the deploy will create does not exist yet.
 */
function PathInput({ value, onChange, placeholder, id }) {
    const [options, setOptions] = useState([]);

    useEffect(() => {
        const asked = value ?? "";
        if (!asked.startsWith("/")) return undefined;
        const timer = setTimeout(() => {
            api.discover.paths(asked).then((result) => setOptions(result.entries.map((entry) => entry.path))).catch(() => setOptions([]));
        }, 250);
        return () => clearTimeout(timer);
    }, [value]);

    return <Suggest id={id} value={value} onChange={onChange} options={options} placeholder={placeholder} />;
}

function Field({ label, help, children, wide = false }) {
    return (
        <div className={wide ? "sm:col-span-2" : ""}>
            <label className="label mb-1">{label}</label>
            {children}
            {help ? <p className="muted mt-1 text-xs">{help}</p> : null}
        </div>
    );
}

function Check({ label, help, checked, onChange }) {
    return (
        <label className="flex cursor-pointer items-start gap-2.5 text-sm">
            <input type="checkbox" className="mt-0.5 size-4 shrink-0" checked={Boolean(checked)} onChange={(event) => onChange(event.target.checked)} />
            <span className="min-w-0">
                {label}
                {help ? <span className="muted block text-xs">{help}</span> : null}
            </span>
        </label>
    );
}

/** A list of plain strings — domains, artifacts, the projects to deploy after. */
function StringList({ value = [], onChange, placeholder, addLabel = "Add" }) {
    return (
        <div className="space-y-1.5">
            {value.map((item, index) => (
                <div key={index} className="flex gap-1.5">
                    <input
                        className="input-mono"
                        value={item}
                        placeholder={placeholder}
                        onChange={(event) => onChange(value.map((existing, at) => (at === index ? event.target.value : existing)))}
                    />
                    <button
                        className="btn-ghost shrink-0 px-2"
                        onClick={() => onChange(value.filter((_, at) => at !== index))}
                        aria-label={`Remove ${item || "entry"}`}
                    >
                        <Trash2 size={13} />
                    </button>
                </div>
            ))}
            <button className="btn-ghost px-2 py-1 text-xs" onClick={() => onChange([...value, ""])}>
                <Plus size={12} /> {addLabel}
            </button>
        </div>
    );
}

/**
 * A list of objects — sources, steps, services, persistent directories.
 *
 * Each entry is a card rather than a table row: a service has seven fields and
 * some of them are long, and a row of seven inputs is unreadable at any width.
 */
function ObjectList({ value = [], onChange, fields, addLabel, describe }) {
    const update = (index, key, next) =>
        onChange(value.map((entry, at) => (at === index ? { ...entry, [key]: next } : entry)));

    return (
        <div className="space-y-2">
            {value.map((entry, index) => (
                <div key={index} className="rounded-lg border border-gray-200 p-3 dark:border-gray-800">
                    <div className="mb-2 flex items-center justify-between gap-2">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                            {describe?.(entry, index) ?? `#${index + 1}`}
                        </span>
                        <button className="btn-ghost px-2 py-0.5" onClick={() => onChange(value.filter((_, at) => at !== index))} aria-label="Remove entry">
                            <Trash2 size={13} />
                        </button>
                    </div>
                    <div className="grid gap-2.5 sm:grid-cols-2">
                        {fields.map((field) => (
                            <Field key={field.key} label={field.label} help={field.help} wide={field.wide}>
                                {field.kind === "checkbox" ? (
                                    <Check label={field.label} checked={entry[field.key]} onChange={(next) => update(index, field.key, next)} />
                                ) : (
                                    <input
                                        className="input-mono"
                                        type={field.kind === "number" ? "number" : "text"}
                                        value={entry[field.key] ?? ""}
                                        placeholder={field.placeholder}
                                        onChange={(event) =>
                                            update(index, field.key, field.kind === "number" ? Number(event.target.value) || null : event.target.value)
                                        }
                                    />
                                )}
                            </Field>
                        ))}
                    </div>
                </div>
            ))}
            <button className="btn-ghost px-2 py-1 text-xs" onClick={() => onChange([...value, {}])}>
                <Plus size={12} /> {addLabel}
            </button>
        </div>
    );
}

/** Env groups are a map, not a list: the name is the key the steps reference. */
function EnvGroups({ value = {}, onChange }) {
    const entries = Object.entries(value);

    const rename = (from, to) => {
        if (!to || to === from) return;
        const next = {};
        for (const [name, group] of entries) next[name === from ? to : name] = group;
        onChange(next);
    };

    return (
        <div className="space-y-2">
            {entries.map(([name, group]) => (
                <div key={name} className="rounded-lg border border-gray-200 p-3 dark:border-gray-800">
                    <div className="mb-2 flex items-center justify-between gap-2">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{name}</span>
                        <button
                            className="btn-ghost px-2 py-0.5"
                            onClick={() => onChange(Object.fromEntries(entries.filter(([other]) => other !== name)))}
                            aria-label={`Remove ${name}`}
                        >
                            <Trash2 size={13} />
                        </button>
                    </div>
                    <div className="grid gap-2.5 sm:grid-cols-2">
                        <Field label="Name" help="What a step's `envFrom` refers to.">
                            <input className="input-mono" defaultValue={name} onBlur={(event) => rename(name, event.target.value.trim())} />
                        </Field>
                        <Field label="File" help="Relative to the project root. Only shared/ survives a release swap.">
                            <input
                                className="input-mono"
                                value={group.file ?? ""}
                                placeholder="shared/server.env"
                                onChange={(event) => onChange({ ...value, [name]: { ...group, file: event.target.value } })}
                            />
                        </Field>
                        <Field label="Required keys" help="Missing any of these fails the deploy before the swap." wide>
                            <StringList
                                value={group.required ?? []}
                                onChange={(next) => onChange({ ...value, [name]: { ...group, required: next } })}
                                placeholder="MONGODB_URI"
                                addLabel="Add key"
                            />
                        </Field>
                    </div>
                </div>
            ))}
            <button
                className="btn-ghost px-2 py-1 text-xs"
                onClick={() => onChange({ ...value, [`group${entries.length + 1}`]: { file: "" } })}
            >
                <Plus size={12} /> Add group
            </button>
        </div>
    );
}

/**
 * Cloudflare's zones, then the names inside one.
 *
 * Two levels because listing every record across twenty-four zones is one API
 * call per zone. It is also the way you think about it: pick the domain, then the
 * subdomain.
 */
function DomainPicker({ open, onClose, onPick }) {
    return (
        <Picker
            open={open}
            onClose={onClose}
            title="Zones"
            hint="From Cloudflare. Pick a zone to see the names in it."
            onPick={(item) => onPick(item.value)}
            load={() =>
                api.discover.zones().then((zones) =>
                    zones.map((zone) => ({
                        id: zone.id,
                        label: zone.name,
                        detail: zone.slug ? `already used by ${zone.slug}` : zone.status,
                        children: true
                    }))
                )
            }
            childTitle={(zone) => `${zone.label} — names`}
            loadChildren={(zone) =>
                api.discover.dns(zone.id).then((result) =>
                    result.records
                        // A zone carries DKIM and verification names nobody would
                        // ever serve a site from. The ones already pointing at this
                        // box are what you came for, so they go first.
                        .map((record) => ({
                            id: `${record.name}-${record.type}`,
                            label: record.name,
                            value: record.name,
                            detail: `${record.type} → ${record.content}${record.slug ? ` · ${record.slug}` : ""}`,
                            badge: record.here === true ? "this box" : record.proxied ? "proxied" : null,
                            badgeClass: record.here === true ? "badge-success" : "badge-queued",
                            rank: record.here === true ? 0 : record.proxied ? 1 : 2
                        }))
                        .sort((a, b) => a.rank - b.rank || a.label.localeCompare(b.label))
                )
            }
        />
    );
}

/** Every repository the token can see, so a source is chosen rather than typed. */
function RepoPicker({ open, onClose, onPick }) {
    return (
        <Picker
            open={open}
            onClose={onClose}
            title="Repositories"
            hint="From GitLab, most recently active first."
            onPick={onPick}
            load={() =>
                api.discover.repos().then((repos) =>
                    repos.map((repo) => ({
                        id: repo.id,
                        label: repo.path,
                        value: repo.ssh,
                        branch: repo.branch,
                        detail: `default branch ${repo.branch ?? "—"}`,
                        badge: repo.onboarded ? "onboarded" : null,
                        badgeClass: "badge-success"
                    }))
                )
            }
            loadChildren={() => Promise.resolve([])}
        />
    );
}

function Section({ title, help, children }) {
    return (
        <section className="card p-4">
            <h3 className="section-title">{title}</h3>
            {help ? <p className="muted mt-0.5 mb-3 text-xs">{help}</p> : <div className="mb-3" />}
            {children}
        </section>
    );
}

/** Fields the form owns. Anything else is passed through untouched. */
const OWNED = [
    "name", "branch", "node", "strategy", "root", "keepReleases", "autoDeploy", "paused",
    "domains", "sources", "install", "steps", "artifacts", "migrations", "services",
    "ecosystem", "envGroups", "persistent", "health", "then"
];

/**
 * What this manifest would do, beside the form that describes it.
 *
 * The server works it out, from the same `withDefaults` the runner uses, so the
 * preview cannot describe a deploy the runner would not perform. It is the answer
 * to "what does this field actually change", which a form alone never gives you.
 */
function Preview({ plan, problems, checking }) {
    if (!plan) return <p className="muted text-xs">{checking ? "checking…" : "make a change to see what it would do"}</p>;

    const Line = ({ children }) => <li className="flex gap-2 text-xs"><span className="muted select-none">·</span><span className="min-w-0 flex-1">{children}</span></li>;

    return (
        <div className="space-y-3 text-xs">
            {problems.length > 0 ? (
                <div className="rounded-lg border border-red-300 p-2 dark:border-red-900">
                    <p className="font-medium text-red-600 dark:text-red-400">{problems.length} problem{problems.length === 1 ? "" : "s"}</p>
                    <ul className="mt-1 space-y-0.5">
                        {problems.map((problem, index) => (
                            <li key={index} className="text-red-600 dark:text-red-400">
                                <code className="font-mono">{problem.path || "manifest"}</code> — {problem.message}
                            </li>
                        ))}
                    </ul>
                </div>
            ) : (
                <p className="badge-success">valid</p>
            )}

            <div>
                <p className="mb-1 font-medium">A deploy would</p>
                <ul className="space-y-1">
                    <Line>run on <strong>Node {plan.node}</strong></Line>
                    {plan.sources.map((source) => (
                        <Line key={source.url}>check out <code className="font-mono">{source.url.split("/").pop()}</code> into <code className="font-mono">{source.into}</code></Line>
                    ))}
                    {plan.envFiles.map((file) => <Line key={file.name}>write <code className="font-mono">{file.file}</code></Line>)}
                    {plan.persistent.map((entry) => <Line key={entry.from}>link <code className="font-mono">{entry.into ?? entry.from}</code> to <code className="font-mono">{entry.from}</code></Line>)}
                    {plan.install ? <Line>install with <code className="font-mono">{plan.install}</code></Line> : null}
                    {plan.steps.map((step) => (
                        <Line key={step.name}>
                            run <strong>{step.name}</strong>: <code className="font-mono">{step.run}</code>
                            {step.cwd !== "." ? <span className="muted"> in {step.cwd}</span> : null}
                            {step.envFrom ? <span className="muted"> with {step.envFrom}</span> : null}
                        </Line>
                    ))}
                    {plan.artifacts.map((artifact) => <Line key={artifact}>check <code className="font-mono">{artifact}</code> exists</Line>)}
                    {plan.migrations ? <Line>run migrations: <code className="font-mono">{plan.migrations}</code></Line> : null}
                    {plan.strategy === "releases" ? <Line>swap <code className="font-mono">current</code> to the new release</Line> : <Line className="muted">deploy in place — no rollback</Line>}
                    {plan.services.map((service) => (
                        <Line key={service.pm2}>reload <strong>{service.pm2}</strong>{service.health ? <span className="muted"> and wait for {service.health.replace(/^https?:\/\/127\.0\.0\.1/, "")}</span> : null}</Line>
                    ))}
                    {plan.then.map((slug) => <Line key={slug}>then deploy <strong>{slug}</strong></Line>)}
                </ul>
            </div>

            {plan.gates.length > 0 ? (
                <div>
                    <p className="mb-1 font-medium">It would stop before the swap if</p>
                    <ul className="space-y-1">
                        {plan.gates.map((gate, index) => <Line key={index}>{gate}</Line>)}
                    </ul>
                </div>
            ) : null}
        </div>
    );
}

export function ManifestForm({ project, problems = [], onSave }) {
    const [schema, setSchema] = useState({});
    const [nodes, setNodes] = useState([]);
    const [branches, setBranches] = useState([]);
    const [picking, setPicking] = useState(null);
    const [plan, setPlan] = useState(null);
    const [live, setLive] = useState([]);
    const [checking, setChecking] = useState(false);
    const [draft, setDraft] = useState(() => {
        const copy = { ...project };
        for (const key of ["_id", "id", "__v", "createdAt", "updatedAt", "lease", "deploymentCounter", "currentRelease", "previousRelease", "lastDeployment", "lastDeploymentId", "lastStatus", "lastDeployedAt", "currentEnvRev", "manifestRev", "slug", "gitlab", "favicon"]) {
            delete copy[key];
        }
        return copy;
    });
    const [advanced, setAdvanced] = useState(false);
    // What the form opened with: while the draft differs, the page is marked
    // dirty and a new build waits for you rather than reloading over it.
    const opened = useRef(null);
    if (opened.current === null) opened.current = JSON.stringify(draft);
    const dirty = JSON.stringify(draft) !== opened.current;

    useEffect(() => {
        api.projects.schema().then((result) => setSchema(result.schema ?? result)).catch(() => {});
        api.discover.nodeVersions().then(setNodes).catch(() => {});
        // Nothing else on mount. Cloudflare is asked when the picker opens: every
        // record across every zone is one API call per zone, twenty-four of them,
        // for a list most visits to this tab never look at.
    }, []);

    useEffect(() => {
        if (!project.gitlab?.projectId) return;
        api.discover.branches(project.gitlab.projectId).then(setBranches).catch(() => {});
    }, [project.gitlab?.projectId]);

    /*
     * Validated as you type, not on save.
     *
     * A mistake is then visible where it was made, and the same request returns
     * what a deploy would do with the draft — so the preview beside the form is
     * never a guess about the server's behaviour.
     */
    useEffect(() => {
        setChecking(true);
        const timer = setTimeout(() => {
            api.projects
                .validate(project.slug, draft)
                .then((result) => {
                    setPlan(result.plan);
                    setLive(result.problems ?? []);
                })
                .catch(() => {})
                .finally(() => setChecking(false));
        }, 400);
        return () => clearTimeout(timer);
    }, [draft, project.slug]);

    const set = (key, value) => setDraft((previous) => ({ ...previous, [key]: value }));
    const help = (key) => schema?.[key]?.help;
    const problemFor = (prefix) => problems.filter((problem) => (problem.path ?? "").startsWith(prefix));

    const extras = Object.keys(draft).filter((key) => !OWNED.includes(key));

    const shownProblems = problems.length ? problems : live;

    return (
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
            <div className="space-y-4">
            <p className="muted text-xs">
                The deploy manifest. Validated on save by the same schema the runner uses. The slug and the GitLab project
                id are fixed — changing either would orphan the webhook, the releases and the history.
            </p>

            <Section title="General">
                <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Name" help={help("name")}>
                        <input className="input" value={draft.name ?? ""} onChange={(event) => set("name", event.target.value)} />
                    </Field>
                    <Field label="Branch" help={branches.length ? `${branches.length} on the remote.` : help("branch")}>
                        <Suggest id="branches" value={draft.branch} onChange={(next) => set("branch", next)} options={branches.map((branch) => ({ value: branch.name, label: branch.default ? "default" : branch.sha }))} />
                    </Field>
                    <Field label="Node major" help={nodes.length ? `Installed: ${nodes.map((entry) => entry.major).join(", ")}. A major that is not there stops the deploy before anything is built.` : help("node")}>
                        <select className="input" value={draft.node ?? ""} onChange={(event) => set("node", event.target.value)}>
                            {/* A manifest can name a major that is no longer installed; it
                                has to stay selectable or the form would silently change it. */}
                            {nodes.some((entry) => entry.major === draft.node) ? null : <option value={draft.node ?? ""}>{draft.node || "—"} (not installed)</option>}
                            {nodes.map((entry) => (
                                <option key={entry.major} value={entry.major}>{entry.major} · {entry.version}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Strategy" help={help("strategy")}>
                        <select className="input" value={draft.strategy ?? "releases"} onChange={(event) => set("strategy", event.target.value)}>
                            {(schema?.strategy?.options ?? ["releases", "in-place"]).map((option) => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Root" help={help("root")} wide>
                        <PathInput id="root-path" value={draft.root} onChange={(next) => set("root", next)} placeholder="/data/project" />
                    </Field>
                    <Field label="Releases to keep" help={help("keepReleases")}>
                        <input type="number" min={1} className="input" value={draft.keepReleases ?? 3} onChange={(event) => set("keepReleases", Number(event.target.value) || 1)} />
                    </Field>
                    <div className="flex flex-col justify-end gap-2 pb-1">
                        <Check label="Deploy on push" help={help("autoDeploy")} checked={draft.autoDeploy ?? true} onChange={(next) => set("autoDeploy", next)} />
                        <Check label="Paused" help={help("paused")} checked={draft.paused ?? false} onChange={(next) => set("paused", next)} />
                    </div>
                </div>
            </Section>

            <Section title="Domains" help={help("domains")}>
                <button className="btn-ghost mb-2 px-2 py-1 text-xs" onClick={() => setPicking("domain")}>
                    <Globe size={12} /> Browse Cloudflare
                </button>
                <StringList
                    value={draft.domains ?? []}
                    onChange={(next) => set("domains", next)}
                    placeholder="example.pt"
                    addLabel="Add domain"
                />
            </Section>

            <Section title="Sources" help={help("sources")}>
                <button className="btn-ghost mb-2 px-2 py-1 text-xs" onClick={() => setPicking("repo")}>
                    <GitBranch size={12} /> Choose a repository
                </button>
                <ObjectList
                    value={draft.sources ?? []}
                    onChange={(next) => set("sources", next)}
                    addLabel="Add source"
                    describe={(entry) => entry.url?.split("/").pop() ?? "new source"}
                    fields={[
                        { key: "url", label: "Repository", placeholder: "git@gitlab.com:you/project.git", help: "SSH only — HTTPS is refused because a token ends up in .git/config.", wide: true },
                        { key: "into", label: "Into", placeholder: ".", help: "Where in the release it is checked out." }
                    ]}
                />
            </Section>

            <Section title="Install" help="How dependencies are installed, once, at the top of the release.">
                <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Command" wide>
                        <input
                            className="input-mono"
                            value={draft.install?.cmd ?? ""}
                            onChange={(event) => set("install", { ...draft.install, cmd: event.target.value })}
                        />
                    </Field>
                    <Check
                        label="Include devDependencies"
                        help="A build needs them; a runtime does not."
                        checked={draft.install?.includeDev}
                        onChange={(next) => set("install", { ...draft.install, includeDev: next })}
                    />
                    <Check
                        label="Reuse node_modules"
                        help="Only when the lockfile and the Node major both match the last release."
                        checked={draft.install?.reuseModules}
                        onChange={(next) => set("install", { ...draft.install, reuseModules: next })}
                    />
                </div>
            </Section>

            <Section title="Build steps" help={help("steps")}>
                <ObjectList
                    value={draft.steps ?? []}
                    onChange={(next) => set("steps", next)}
                    addLabel="Add step"
                    describe={(entry, index) => entry.name || `step ${index + 1}`}
                    fields={[
                        { key: "name", label: "Name", placeholder: "build-web", help: "Labels the step in the live log." },
                        { key: "cwd", label: "Working directory", placeholder: "web", help: "Relative to the release." },
                        { key: "run", label: "Command", placeholder: "npm run build", wide: true },
                        { key: "envFrom", label: "Environment from", placeholder: "web", help: "An env group, for a build that needs VITE_* inlined." }
                    ]}
                />
            </Section>

            <Section title="Artifacts" help={help("artifacts")}>
                <StringList value={draft.artifacts ?? []} onChange={(next) => set("artifacts", next)} placeholder="web/dist/index.html" addLabel="Add artifact" />
            </Section>

            <Section title="Services" help={help("services")}>
                <ObjectList
                    value={draft.services ?? []}
                    onChange={(next) => set("services", next)}
                    addLabel="Add service"
                    describe={(entry, index) => entry.pm2 || `service ${index + 1}`}
                    fields={[
                        { key: "pm2", label: "PM2 name", placeholder: "myproject-server" },
                        { key: "health", label: "Health URL", placeholder: "http://127.0.0.1:3000/health", help: "Loopback only. Anything under 400 counts as up." },
                        { key: "cwd", label: "Working directory", placeholder: "server" },
                        { key: "script", label: "Script", placeholder: "src/index.js" },
                        { key: "args", label: "Arguments", placeholder: "start --port 3030" },
                        { key: "instances", label: "Instances", kind: "number", placeholder: "1", help: "More than one runs in cluster mode." },
                        { key: "killTimeoutMs", label: "Kill timeout (ms)", kind: "number", placeholder: "1600", help: "How long PM2 waits for a clean exit before SIGKILL." },
                        { key: "group", label: "Environment group", placeholder: "server", help: "Passed as --env-file by the generated ecosystem." }
                    ]}
                />
                {problemFor("services").map((problem, index) => (
                    <p key={index} className="mt-2 text-xs text-red-600 dark:text-red-400">
                        <code className="font-mono">{problem.path}</code> — {problem.message}
                    </p>
                ))}
            </Section>

            <Section title="Environment groups" help={help("envGroups")}>
                <EnvGroups value={draft.envGroups ?? {}} onChange={(next) => set("envGroups", next)} />
            </Section>

            <Section title="Persistent directories" help="Anything the application writes: uploads, generated media, a cache. A release is replaced on every deploy and deleted a few deploys later.">
                <ObjectList
                    value={draft.persistent ?? []}
                    onChange={(next) => set("persistent", next)}
                    addLabel="Add directory"
                    describe={(entry, index) => entry.path || `directory ${index + 1}`}
                    fields={[
                        { key: "path", label: "Path", placeholder: "shared/uploads", help: "Where the data lives, under the project root." },
                        { key: "into", label: "Linked into the release at", placeholder: "uploads", help: "Leave empty only if the app is given the absolute path." }
                    ]}
                />
            </Section>

            <Section title="Process configuration and migrations">
                <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Ecosystem file" help={help("ecosystem")} wide>
                        <input className="input-mono" value={draft.ecosystem ?? ""} placeholder="deploy/ecosystem.config.cjs" onChange={(event) => set("ecosystem", event.target.value || null)} />
                    </Field>
                    <Field label="Migrations" help={help("migrations")} wide>
                        <input className="input-mono" value={draft.migrations ?? ""} placeholder="npm run migrate" onChange={(event) => set("migrations", event.target.value || null)} />
                    </Field>
                    <Field label="Health retries" help="How many times each service is polled.">
                        <input type="number" min={1} className="input" value={draft.health?.retries ?? 30} onChange={(event) => set("health", { ...draft.health, retries: Number(event.target.value) || 1 })} />
                    </Field>
                    <Field label="Health interval (ms)">
                        <input type="number" min={100} step={100} className="input" value={draft.health?.intervalMs ?? 2000} onChange={(event) => set("health", { ...draft.health, intervalMs: Number(event.target.value) || 100 })} />
                    </Field>
                </div>
            </Section>

            <Section title="Then deploy" help={help("then")}>
                <StringList value={draft.then ?? []} onChange={(next) => set("then", next)} placeholder="another-project-slug" addLabel="Add project" />
            </Section>

            {/* A field this form does not know about must stay editable. */}
            <Section title="Advanced" help={extras.length ? `Fields this form does not lay out: ${extras.join(", ")}.` : "The manifest as the server stores it."}>
                <button className="btn-ghost px-2 py-1 text-xs" onClick={() => setAdvanced((value) => !value)} aria-expanded={advanced}>
                    {advanced ? "Hide" : "Show"} JSON
                </button>
                {advanced ? (
                    <textarea
                        className="input mt-2 h-80 resize-y font-mono text-xs leading-relaxed"
                        value={JSON.stringify(draft, null, 4)}
                        spellCheck={false}
                        onChange={(event) => {
                            try {
                                setDraft(JSON.parse(event.target.value));
                            } catch {
                                // Half-typed JSON is not an error worth shouting about.
                            }
                        }}
                    />
                ) : null}
            </Section>

            <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-gray-200 bg-gray-50 py-3 dark:border-gray-800 dark:bg-gray-950" data-dirty={dirty ? "" : undefined}>
                {shownProblems.length > 0 ? (
                    <span className="text-xs text-red-600 dark:text-red-400">{shownProblems.length} problem{shownProblems.length === 1 ? "" : "s"}</span>
                ) : null}
                <button className="btn-primary" onClick={() => onSave(draft)} disabled={shownProblems.length > 0}>Save manifest</button>
            </div>
            </div>

            <DomainPicker
                open={picking === "domain"}
                onClose={() => setPicking(null)}
                onPick={(domain) => set("domains", [...(draft.domains ?? []).filter((existing) => existing !== domain), domain])}
            />
            <RepoPicker
                open={picking === "repo"}
                onClose={() => setPicking(null)}
                onPick={(repo) => {
                    // A repository answers two fields at once, and the branch is the
                    // one people forget when they paste a URL.
                    set("sources", [{ url: repo.value, into: draft.sources?.[0]?.into ?? "." }, ...(draft.sources ?? []).slice(1)]);
                    if (repo.branch) set("branch", repo.branch);
                }}
            />

            <aside className="card p-3 lg:sticky lg:top-24" aria-label="Preview">
                <h3 className="section-title mb-2">Preview</h3>
                <Preview plan={plan} problems={shownProblems} checking={checking} />
            </aside>
        </div>
    );
}
